import { useEffect, useRef } from 'react';
import { API } from '@weave/schema-gen-ts/dist/schemas/announcements/v1/announcements.pb';
import { useQuery, useQueryClient } from 'react-query';
import { useGlobalBannerStore } from '@frontend/global-info-banner';
import { bindHTTP } from '@frontend/schema';
import { useWebsocketEventSubscription } from '@frontend/websocket';

export const AnnouncementAPI = bindHTTP(API);

export const AnnouncementBanner = () => {
  const { banners, addBanner, updateBanner, removeBanner } = useGlobalBannerStore([
    'banners',
    'addBanner',
    'updateBanner',
    'removeBanner',
  ]);
  const initializedRef = useRef(false);
  const queryClient = useQueryClient();

  // Also need to query for announcement data to display when the app loads
  const { data } = useQuery({
    queryKey: 'announcements-list',
    queryFn: async () => {
      const response = await AnnouncementAPI.GetLiveAnnouncements({});
      return response.announcements;
    },
  });

  useEffect(() => {
    if (data && !initializedRef.current) {
      data.forEach((announcement) => {
        const payload = announcement.payload;
        // Right now we don't persist dismissed announcements, we might be able to use user prefs service to do this later
        if (payload && !banners.find((b) => b.id === announcement.id)) {
          addBanner({
            id: announcement.id,
            type: payload.type,
            priority: payload.priority,
            isDismissible: payload.isDismissible ?? true,
            title: payload.title ?? '',
            message: payload.message ?? '',
            path: payload.path,
            action: payload.action
              ? {
                  label: payload.action.label,
                  onClick: () => window.open(payload.action.href, '_blank'),
                }
              : undefined,
            // trackingId: announcement.trackingId!,
          });
        }
      });
      initializedRef.current = true;
    }
  }, [data, addBanner, banners]);

  useWebsocketEventSubscription('AnnouncementAdd', (payload) => {
    const {
      id,
      params: { title, type, priority, isDismissible, message, path, action, trackingId },
    } = payload;
    if (payload && !banners.find((b) => b.id === id)) {
      addBanner({
        id,
        type,
        priority,
        isDismissible,
        title,
        message,
        path,
        action: action ? { label: action.label, onClick: () => window.open(action.href, '_blank') } : undefined,
        trackingId,
      });

      queryClient.setQueryData('announcements-list', (old: typeof data) => [
        ...(old ?? []),
        { id, payload: { type, priority, isDismissible, title, message, path, action, trackingId } },
      ]);
    }
  });

  useWebsocketEventSubscription('AnnouncementRemove', (payload) => {
    const { id } = payload;
    removeBanner(id);

    queryClient.setQueryData('announcements-list', (old: typeof data) => (old ?? []).filter((a) => a.id !== id));
  });

  useWebsocketEventSubscription('AnnouncementUpdate', (payload) => {
    const {
      id,
      params: { ...updates },
    } = payload;

    const action = updates.action;
    updateBanner(id, {
      ...updates,
      action: action ? { label: action.label, onClick: () => window.open(action.href, '_blank') } : undefined,
    });

    queryClient.setQueryData('announcements-list', (old: typeof data) =>
      (old ?? []).map((a) => (a.id === id ? { ...a, payload: { ...a.payload, ...updates } } : a))
    );
  });

  return null;
};
